import {
  IconButton,
  type IconButtonProps,
} from "$components/button/icon-button";
import { CardColors } from "$components/card";
import { FormattedDate } from "$components/date/formatted-date";
import { BinIcon } from "$components/icons";
import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import {
  type Amp,
  RXDB_AMP_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/amp";
import {
  type Prescription,
  RXDB_PRESCRIPTION_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/prescription";
import {
  RXDB_VMP_COLLECTION_NAME,
  type Vmp,
} from "$global-providers/rxdb-provider/collections/vmp";
import { doseMessages } from "$routes/patient/[:patientId]/inpatient/_lib/prescription/hooks/strings";
import { gql } from "@apollo/client";
import { styled, Typography } from "@material-ui/core";
import { ClinicalProductType, DispensationStrategy } from "elephant-web/gql";
import { FormattedMessage, useIntl } from "react-intl";
import { useRxCollection, useRxData } from "rxdb-hooks";
import {
  useGetDispensedSkuQuery,
  useUserForPrescriptionStatusQuery,
} from "./__generated__/prescription-history-card.gql";
import { DispensationStatusFooter } from "./dispensation-status-footer";
import { CardHeader, PatientHistoryCard } from "./history-card";
import { dosageCount } from "./styled-dosage-string";

const BinButton = (props: IconButtonProps) => (
  <IconButton color="error" aria-label="delete" title="delete" {...props}>
    <BinIcon fill="currentColor" />
  </IconButton>
);

gql`
  query UserForPrescriptionStatus($userId: String!) {
    user(_id: $userId) {
      person {
        firstname
        lastname
      }
    }
  }
`;

gql`
  query GetDispensedSku($input: GetSkuByIdInput!, $origins: [String]) {
    getSkuById(input: $input) {
      externalReferences(origins: $origins) {
        foreignId
        origin
      }
      name
    }
  }
`;

const ContentContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  marginTop: theme.spacing(3),
}));

const Row = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  [theme.breakpoints.up("sm")]: {
    flexDirection: "row",
    gap: theme.spacing(6),
  },
}));

export const PrescriptionHistoryCard: FC<{
  prescription: {
    id?: string;
    title?: string;
    rate?: Prescription["rate"];
    duration?: Prescription["frequency"];
    dose: Prescription["dosage"];
    note?: Prescription["note"];
    clinicalProductFreetext?: string;
    clinicalProductId?: string;
    clinicalProductType?: ClinicalProductType;
    prescribedByUserId: string;
    stopsAt: string;
    createdAt: string;
  };
  dispensation?: {
    dispensedByUserId: string;
    createdAt: string;
    dispensationStrategy: DispensationStrategy;
    quantity: number;
    skuId?: string;
  };
}> = ({
  prescription: {
    id,
    rate,
    duration,
    dose,
    note,
    clinicalProductFreetext,
    clinicalProductId,
    clinicalProductType,
    prescribedByUserId,
    stopsAt,
    createdAt,
  },
  dispensation,
}) => {
  const { formatMessage } = useIntl();
  const [deletePrescriptionsFlag] = useFeatureFlag(
    featureFlags.prescriptionDelete,
  );

  const prescriptionCollection = useRxCollection<Prescription>(
    RXDB_PRESCRIPTION_COLLECTION_NAME,
  );

  const { data: prescriberData } = useUserForPrescriptionStatusQuery({
    variables: { userId: prescribedByUserId },
  });
  const { data: dispenserData } = useUserForPrescriptionStatusQuery({
    variables: { userId: dispensation?.dispensedByUserId },
    skip: !dispensation?.dispensedByUserId,
  });
  const prescribingUser = prescriberData?.user;
  const dispensingUser = dispenserData?.user;

  const prescribedBy = [
    prescribingUser?.person?.firstname,
    prescribingUser?.person?.lastname,
  ].join(" ");

  const dispensedBy = [
    dispensingUser?.person?.firstname,
    dispensingUser?.person?.lastname,
  ].join(" ");

  const { data: dispensedSku } = useGetDispensedSkuQuery({
    variables: { input: { id: dispensation?.skuId } },
    skip: !dispensation?.skuId,
  });

  // Query VMP collection if product type is VMP or undefined (for backward compatibility)
  const { result: vmpResult } = useRxData<Vmp>(
    RXDB_VMP_COLLECTION_NAME,
    (collection) =>
      clinicalProductId &&
      (!clinicalProductType ||
        clinicalProductType === ClinicalProductType.Vmp) &&
      collection.find({ selector: { id: clinicalProductId } }),
  );

  // Query AMP collection if product type is AMP
  const { result: ampResult } = useRxData<Amp>(
    RXDB_AMP_COLLECTION_NAME,
    (collection) =>
      clinicalProductId &&
      clinicalProductType === ClinicalProductType.Amp &&
      collection.find({ selector: { id: clinicalProductId } }),
  );

  // Get title from appropriate collection
  const label =
    vmpResult[0]?.title ?? ampResult[0]?.title ?? clinicalProductFreetext;

  const showTotalToDispense =
    (dose.unit === "tablet" || dose.unit === "capsule") && rate && duration;

  const translatedDoseUnit = doseMessages[dose.unit]
    ? formatMessage(doseMessages[dose.unit], { value: dose.value })
    : dose.unit;

  return (
    <PatientHistoryCard
      id={id}
      defaultExpanded={!dispensation}
      color={
        dispensation?.dispensationStrategy ===
        DispensationStrategy.IssuedPaperPrescription
          ? CardColors.DISABLED
          : CardColors.PRESCRIPTION
      }
      HeaderComponent={() => (
        <CardHeader
          color={CardColors.PRESCRIPTION}
          category="Prescription"
          title={label}
          id={id}
          extras={
            deletePrescriptionsFlag &&
            !dispensation && (
              <BinButton
                aria-label="Delete prescription"
                onClick={() => prescriptionCollection.bulkRemove([id])}
              />
            )
          }
        />
      )}
      FooterComponent={() => (
        <DispensationStatusFooter
          dispensation={
            dispensation
              ? {
                  createdAt,
                  dispensationStrategy: dispensation.dispensationStrategy,
                  dispensedBy,
                }
              : undefined
          }
          prescribedBy={prescribedBy}
          prescriptionValidUntil={stopsAt}
        />
      )}
    >
      <ContentContainer>
        <Row>
          <Typography
            variant="body2"
            component="p"
            style={{ minWidth: "180px" }}
          >
            <strong>
              <FormattedMessage
                id="patient.prescriptions.dispense.prescription.details.datePrescribed"
                defaultMessage="Date prescribed:"
              />
            </strong>
            &nbsp;
            <FormattedDate value={createdAt} />
          </Typography>
          <Typography variant="body2" component="p">
            <strong>
              <FormattedMessage
                id="patient.prescriptions.dispense.prescription.prescribedBy"
                defaultMessage="Prescribed by:"
              />
            </strong>
            &nbsp;
            {prescribedBy}
          </Typography>
        </Row>
        <Row>
          <Typography
            variant="body2"
            component="p"
            style={{ minWidth: "180px" }}
          >
            <strong>
              <FormattedMessage
                id="patient.prescriptions.dispense.prescription.singleDosage"
                defaultMessage="Single dosage:"
              />
              &nbsp;
            </strong>
            {`${dose.value} ${translatedDoseUnit}`}
          </Typography>
          {showTotalToDispense && (
            <Typography variant="body2" component="p">
              <strong>
                <FormattedMessage
                  id="patient.history.prescription.dosesToDispense"
                  defaultMessage="Doses to dispense:"
                />
              </strong>
              &nbsp;
              {dosageCount(rate, duration, dose)}
            </Typography>
          )}
        </Row>

        {/* TODO: re-add prescribing location? */}

        {dispensedSku?.getSkuById && (
          <Row>
            {dispensation.dispensationStrategy ===
              DispensationStrategy.ProvidedAlternativeClinicalProduct && (
              <Typography variant="body2" component="p">
                <strong>
                  <FormattedMessage
                    id="patient.history.prescription.medicineDispensed"
                    defaultMessage="Medicine dispensed:"
                  />
                </strong>
                &nbsp;
                {dispensedSku.getSkuById.name}
              </Typography>
            )}
            {dispensation?.quantity ? (
              <Typography variant="body2" component="p">
                <strong>
                  <FormattedMessage
                    id="patient.history.prescription.quantityDispensed"
                    defaultMessage="Quantity dispensed:"
                  />
                </strong>
                &nbsp;
                {`${dispensation.quantity} `}
                <FormattedMessage
                  id="patient.history.prescription.units"
                  defaultMessage="units"
                />
              </Typography>
            ) : null}
          </Row>
        )}

        {/* TODO: re-add dispensing location? */}

        {note && (
          <Row>
            <Typography variant="body2" component="p">
              <strong>
                <FormattedMessage
                  id="patient.new.consultation.review.notes"
                  defaultMessage="Notes:"
                />
              </strong>
              &nbsp;
              {`${note} `}
            </Typography>
          </Row>
        )}
      </ContentContainer>
    </PatientHistoryCard>
  );
};
